/**
 * WeeksIndexView — list every generated weekly report from
 * `GET /api/weeks` newest-first. Each row links to `#/week/<id>`.
 *
 * Reads cache first, then triggers `fetchWeeks()` and stores the
 * response. The view stays empty (`暂无周报`) when the list is empty;
 * it does NOT auto-redirect to the daily feed.
 */

import { el, setChildren } from "../dom";
import { ApiError, fetchWeeks } from "../api";
import { setCacheEntry, getStore } from "../store";
import { navigate } from "../router";
import type { WeekStatus, WeekSummary } from "../types";

export function renderWeeksIndexView(): HTMLElement {
  const root = el("section", { class: "view view-weeks", "data-view": "weeks" }, [
    el("div", { class: "view-title" }, [
      el("h1", null, "周报索引"),
      el("p", { class: "view-title-sub" }, "正在加载周报列表…"),
    ]),
    el("div", { class: "status-row" }, [
      el("div", { class: "spinner", "aria-hidden": "true" }),
      el("div", { class: "status-row-title" }, "正在加载…"),
    ]),
  ]);

  void load();
  return root;

  async function load(): Promise<void> {
    const cached = getStore().cache.weeks;
    if (cached) {
      paint(cached);
      return;
    }

    try {
      const weeks = await fetchWeeks();
      setCacheEntry("weeks", weeks);
      paint(weeks);
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "周报索引"),
          el("p", { class: "view-title-sub" }, "无法加载"),
        ]),
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "无法加载"),
          el(
            "div",
            { class: "status-row-detail" },
            error ? `${error.status} · ${error.message}` : "网络错误，请稍后重试。",
          ),
        ]),
      ]);
    }
  }

  function paint(weeks: WeekSummary[]): void {
    if (weeks.length === 0) {
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "周报索引"),
          el("p", { class: "view-title-sub" }, "按日期倒序列出全部已生成的学术周报"),
        ]),
        el("div", { class: "status-row" }, [
          el("div", { class: "status-row-title" }, "暂无周报"),
          el(
            "div",
            { class: "status-row-detail" },
            "尚未生成任何学术周报。请等待每周一 06:00（北京时间）自动更新，或通过 /api/regenerate 手动触发。",
          ),
        ]),
      ]);
      return;
    }

    const table = el("div", { class: "weeks-table" });
    const head = el("div", { class: "weeks-table-row weeks-table-head" }, [
      el("div", { class: "weeks-table-cell is-head" }, "周次"),
      el("div", { class: "weeks-table-cell is-head" }, "日期范围"),
      el("div", { class: "weeks-table-cell is-head" }, "纳入"),
      el("div", { class: "weeks-table-cell is-head" }, "排除"),
      el("div", { class: "weeks-table-cell is-head" }, "状态"),
    ]);
    table.appendChild(head);

    for (const w of weeks) {
      table.appendChild(renderRow(w));
    }

    setChildren(root, [
      el("div", { class: "view-title" }, [
        el("h1", null, "周报索引"),
        el("p", { class: "view-title-sub" }, `共 ${weeks.length} 期周报 · 按日期倒序`),
      ]),
      el("div", { class: "main-pane view-body" }, [table]),
    ]);
  }

  function renderRow(w: WeekSummary): HTMLElement {
    const onClick = (ev: Event): void => {
      const me = ev as MouseEvent;
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
      ev.preventDefault();
      navigate(`/week/${encodeURIComponent(w.id)}`);
    };

    return el("a", {
      class: "weeks-table-row weeks-table-body",
      href: `#/week/${encodeURIComponent(w.id)}`,
      onclick: onClick,
    }, [
      el("div", { class: "weeks-table-cell" }, w.id),
      el("div", { class: "weeks-table-cell" }, `${w.start_date} ~ ${w.end_date}`),
      el("div", { class: "weeks-table-cell" }, String(w.included_count)),
      el("div", { class: "weeks-table-cell" }, String(w.excluded_count)),
      el("div", { class: "weeks-table-cell" }, [
        el("span", { class: `weeks-status is-${w.status}` }, statusLabel(w.status)),
      ]),
    ]);
  }
}

function statusLabel(s: WeekStatus): string {
  switch (s) {
    case "complete":
      return "完成";
    case "partial":
      return "部分";
    case "pending":
      return "等待中";
    case "failed":
      return "失败";
    default:
      return s;
  }
}